// Single source of truth for the built-in marketing navigation.
//
// These constants are BOTH the fallback rendered when the database is
// unreachable/empty AND the payload written by the admin "Load / Reset to
// defaults" action — so there is only ever one definition of the default nav.

export type NavLinkType = "route" | "hash" | "external" | "action" | "none";
export type NavVisibility = "all" | "logged_out" | "logged_in";

export interface NavItem {
  id: string;
  label: string;
  link_type: NavLinkType;
  href: string | null;
  open_in_new_tab: boolean;
  action_key: string | null;
  visibility: NavVisibility;
  is_active: boolean;
  starts_at: string | null;
  ends_at: string | null;
  is_protected: boolean;
  sort_order: number;
  parent_id: string | null;
  menu_id: string;
  children: NavItem[];
}

export type NavLocation = "header" | "footer" | "onboarding" | "header_mega";

/** Which header menu renders publicly; stored in admin_settings.header_menu_style. */
export type HeaderMenuStyle = "simple" | "mega";

export interface NavMenu {
  id: string;
  location: NavLocation;
  title: string | null;
  sort_order: number;
  is_active: boolean;
  items: NavItem[];
}

/** Built-in actions an item can trigger instead of navigating. */
export const NAV_ACTIONS: { key: string; label: string }[] = [
  { key: "bug_report", label: "Open Bug Report dialog" },
];

/** Suggested destinations shown in the admin href picker. */
export const KNOWN_ROUTES: string[] = [
  "/",
  "/podcasts",
  "/studios",
  "/blog",
  "/help",
  "/about",
  "/careers",
  "/partners",
  "/why-you-should-join-podgo",
  "/privacy",
  "/terms",
  "/dmca",
  "/cookies",
  "/gdpr",
  "/auth",
  "/dashboard",
];

type ItemSeed = Partial<NavItem> & { label: string };

let seedCounter = 0;
const item = (menuId: string, sort: number, seed: ItemSeed): NavItem => ({
  id: `default-${menuId}-${sort}-${seedCounter++}`,
  menu_id: menuId,
  parent_id: null,
  label: seed.label,
  link_type: seed.link_type ?? "route",
  href: seed.href ?? null,
  open_in_new_tab: seed.open_in_new_tab ?? false,
  action_key: seed.action_key ?? null,
  visibility: seed.visibility ?? "all",
  is_active: true,
  starts_at: null,
  ends_at: null,
  is_protected: seed.is_protected ?? false,
  sort_order: sort,
  children: (seed.children ?? []) as NavItem[],
});

const buildMenu = (
  id: string,
  location: NavLocation,
  title: string | null,
  sort: number,
  seeds: ItemSeed[],
): NavMenu => ({
  id,
  location,
  title,
  sort_order: sort,
  is_active: true,
  items: seeds.map((s, i) => item(id, i, s)),
});

export const DEFAULT_HEADER_MENU: NavMenu = buildMenu("default-header", "header", null, 0, [
  { label: "Features", link_type: "hash", href: "/#features" },
  { label: "How It Works", link_type: "hash", href: "/#how-it-works" },
  { label: "Pricing", link_type: "hash", href: "/#pricing" },
  { label: "Podcasts", href: "/podcasts" },
  { label: "Book A Studio", href: "/studios" },
  { label: "News", href: "/blog" },
  { label: "Why You Should Join PODGO?", href: "/why-you-should-join-podgo" },
]);

export const DEFAULT_FOOTER_MENUS: NavMenu[] = [
  buildMenu("default-footer-product", "footer", "Product", 0, [
    { label: "Features", link_type: "hash", href: "#features" },
    { label: "Pricing", link_type: "hash", href: "#pricing" },
    { label: "How It Works", link_type: "hash", href: "#how-it-works" },
    { label: "API (Coming Soon)", link_type: "none" },
  ]),
  buildMenu("default-footer-resources", "footer", "Resources", 1, [
    { label: "Help Center", href: "/help" },
    { label: "Knowledgebase", href: "/knowledgebase" },
    { label: "News", href: "/blog" },
    {
      label: "Report a Bug",
      link_type: "action",
      action_key: "bug_report",
      is_protected: true,
    },
  ]),
  buildMenu("default-footer-company", "footer", "Company", 2, [
    { label: "About", href: "/about" },
    { label: "Careers", href: "/careers" },
    { label: "Contact", href: "/help" },
    { label: "Partners", href: "/partners" },
  ]),
  buildMenu("default-footer-legal", "footer", "Legal", 3, [
    { label: "Privacy Policy", href: "/privacy" },
    { label: "Terms of Service", href: "/terms" },
    { label: "DMCA / Copyright Policy", href: "/dmca" },
    { label: "Cookie Policy", href: "/cookies" },
    { label: "GDPR Compliance", href: "/gdpr" },
    { label: "Ads.txt", link_type: "external", href: "/ads.txt", open_in_new_tab: true },
  ]),
];

/**
 * Onboarding ships with no navigation by default — the header renders as a
 * centered logo until an admin adds links in Theme Options.
 */
export const DEFAULT_ONBOARDING_MENUS: NavMenu[] = [];

/** Mega menu columns: each menu is one top-level heading whose links open in a wide panel. */
export const DEFAULT_MEGA_MENUS: NavMenu[] = [
  buildMenu("default-mega-platform", "header_mega", "Platform", 0, [
    { label: "Features", link_type: "hash", href: "/#features" },
    { label: "How It Works", link_type: "hash", href: "/#how-it-works" },
    { label: "Pricing", link_type: "hash", href: "/#pricing" },
    { label: "Why Join PODGO?", href: "/why-you-should-join-podgo" },
  ]),
  buildMenu("default-mega-discover", "header_mega", "Discover", 1, [
    { label: "Podcasts", href: "/podcasts" },
    { label: "Book A Studio", href: "/studios" },
    { label: "Partners", href: "/partners" },
  ]),
  buildMenu("default-mega-resources", "header_mega", "Resources", 2, [
    { label: "Knowledgebase", href: "/knowledgebase" },
    { label: "News", href: "/blog" },
    { label: "Help Center", href: "/help" },
  ]),
  buildMenu("default-mega-links", "header_mega", "Why Join PODGO?", 0, [
    { label: "Overview", href: "/why-you-should-join-podgo" },
    { label: "Stop juggling tools", href: "/why-you-should-join-podgo?story=simplicity" },
    { label: "Make more money", href: "/why-you-should-join-podgo?story=money" },
  ]),
];

export const DEFAULT_MENUS: Record<NavLocation, NavMenu[]> = {
  header: [DEFAULT_HEADER_MENU],
  footer: DEFAULT_FOOTER_MENUS,
  onboarding: DEFAULT_ONBOARDING_MENUS,
  header_mega: DEFAULT_MEGA_MENUS,
};
